import React, { FC } from 'react';
import { TContentHubHeroBannerSeriesDetailProps } from './types/productHeroBannerSeriesDetail.types';
import { isEditorActive, RichText, Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { ProductHeroBannerSeriesDetailModel } from './models/productHeroBannerSeriesDetail.model';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import useExperienceEditorPlaceholder from '../../hooks/useExperienceEditorPlaceholder';
import useMediaQuery from '../../hooks/useMediaQuery';
import '../../styles/@shared/hero-banner.scss';
import './styles.scss';
import CTA from '../@shared/CTA';
import FadeDown from '../@shared/fadeDownEffect';
import ctaTypes from '../../enums/CTA.enums';
import Breakpoints from '../../constants/breakpoints.constants';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import { targetLinkdsk } from '../../utils/targetDskLink';
import { withTranslation, WithTranslation } from 'react-i18next';
import InfoToolTip from '../@shared/infoToolTip';
import { isScrolling } from '../../utils/scroll.utils';
import { windowService, documentService } from '../../utils/window.utils';
import useHasComponentById from '../sitecoreJssForm/hooks/useHasComponentById';
import { specialOffers } from '../../constants/productspecialoffers.constants';
import { alertMessage } from '../../constants/alertMessage.constants';
import useGetBuildAndPriceLink from '../../hooks/useGetBuildAndPriceLink';
import DictionaryTerm from '../@shared/dictionaryTerm';
import { appendRootToInternalLink } from '../../utils/appendRootToInternalLink.utils';
import Media from './components/media';

const ProductHeroBannerSeriesDetail: FC<WithTranslation & TContentHubHeroBannerSeriesDetailProps> = (props) => {
  const { t } = props;
  const apiResponse = isEmptyResponse(props);
  const model = new ProductHeroBannerSeriesDetailModel(props);
  const isPlaceholderShown = useExperienceEditorPlaceholder(props);
  const isMobile = useMediaQuery(`(max-width: ${Breakpoints['tbl-p']}px)`);
  const placeholderData = props.sitecoreContext?.route?.placeholders['jss-main'] || [];
  const specialOffersEle = useHasComponentById(specialOffers);
  const isSpecialOfferCTAExist = model.hasSpecialOffers && specialOffersEle && model.primaryLink?.label;

  const handleOnClick = () => {
    if (isScrolling()) return;
    const topOffsetElement = documentService()?.getElementById(specialOffers);
    const alertMessageElement = documentService()?.getElementById(alertMessage);

    if (topOffsetElement) {
      let headerOffset = 85;

      if (alertMessageElement) {
        headerOffset += alertMessageElement.clientHeight;
      }
      const elementPosition = topOffsetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + windowService()?.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };
  const buildAndPriceUrl = useGetBuildAndPriceLink('series');
  
  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  let delay = 1; // in seconds
  const findNextDelay = () => delay++ * 1000;

  return (
      <div className="hero-banner-series-detail__wrapper">
        <div className="hero-banner hero-banner--series-detail" data-text-color={model?.textColor}>
          {/* Need to update useHeroBanner hook if the images sent to Picture component  are changed */}
          <Media model={model}
          />
          <div className="hero-banner__content hero-banner__content--align-top">
            {model.eyebrow && (
              <FadeDown duration={1000} delay={findNextDelay()}>
                <Text
                  tag="p"
                  field={{ value: model.eyebrow }}
                  className={`hero-banner-eyebrow hero-banner--text-color-${model?.textColor}`}
                  editable={false}
                />
              </FadeDown>
            )}
            {model.title && (
              <FadeDown duration={1000} delay={findNextDelay()}>
                <Text
                  tag="h1"
                  field={{ value: model.title }}
                  className={`hero-banner__title hero-banner-title hero-banner--text-color-${model?.textColor}`}
                  editable={false}
                />
              </FadeDown>
            )}
            {model.msrp ? (
              <FadeDown duration={1000} delay={findNextDelay()}>
                <div className={`hero-banner-msrp-tooltip info-tooltip-sep__tooltip--text-color-${model?.textColor}`}>
                  <span className="hero-banner-msrp-tooltip-Title">
                    <Text
                      tag="p"
                      field={{ value: t('cnhi-startingFrom') }}
                      className={`hero-banner-msrp-tooltip-Title--text hero-banner--text-color-${model?.textColor}`}
                      editable={false}
                    />
                    <RichText
                      tag="div"
                      field={{ value: model.msrp }}
                      className={`hero-banner-msrp-tooltip-Title--msrp hero-banner--text-color-${model?.textColor} rich-text`}
                      editable={false}
                    />
                  </span>
                  <InfoToolTip showIcon={true} width={300}>
                    <div className="info-tooltip-sep__tooltip__wrapper hero-banner-tooltip">
                      <Text
                        field={{ value: t('cnhi-MsrpTooltipTitle') }}
                        tag="h2"
                        className="info-tooltip-sep__tooltip__title"
                        editable={false}
                      />
                      <Text
                        field={{ value: t('cnhi-MsrpTooltipDescription') }}
                        tag="div"
                        className="info-tooltip-sep__tooltip__body"
                        editable={false}
                      />
                    </div>
                  </InfoToolTip>
                </div>
              </FadeDown>
            ) : null}
          {!isMobile && (model.isPrimaryLinkExist || model.isSecondaryLinkExist || isSpecialOfferCTAExist || (model?.enableBuildPriceCTA && buildAndPriceUrl)) ? (
              <FadeDown duration={1000} delay={findNextDelay()}>
                <div className="hero-banner__button-wrapper">
                {model?.enableBuildPriceCTA && (
                    <CTA type={ctaTypes.primaryOrange}
                      href={appendRootToInternalLink(buildAndPriceUrl)}
                    >
                      <DictionaryTerm keyword={'cnhi-build-price'} />
                    </CTA>
                  )}
                {isSpecialOfferCTAExist ? (
                  <CTA onClick={() => handleOnClick()} type={ctaTypes.primaryOrange}>
                    {model.primaryLink?.label}
                  </CTA>
                ) : (
                  model?.isPrimaryLinkExist && (
                    <CTA
                      type={ctaTypes.primaryOrange}
                      field={{
                        value: {
                          href: model.primaryCTAUrl,
                          text: model.primaryLink?.label,
                        },
                      }}
                      target={targetLinkdsk({ value: { href: model?.primaryLink?.url, target: model?.primaryLink?.target } }, !isMobile)}
                    />
                  ))}
                  {model.isSecondaryLinkExist && (
                    <CTA
                      type={ctaTypes.primaryWhite}
                      field={{
                        value: {
                          href: model.secondaryCTAUrl,
                          text: model.secondaryLink?.label,
                        },
                      }}
                      target={targetLinkdsk({ value: { href: model?.primaryLink?.url, target: model?.primaryLink?.target } }, !isMobile)}
                    />
                  )}
                </div>
              </FadeDown>
            ) : null}
          </div>
      </div>
            {model.summarySpecs?.length > 0 ? (
              <FadeDown duration={1000} delay={findNextDelay()}>
                <div className="hero-banner-container" specs-data-text-color={model?.quickSpecsColor}>
                  <div className={`hero-banner__specs-wrapper hero-banner__specs-wrapper--${model?.quickSpecsColor}`}>
                    {model.summarySpecs.map((item) => (
                <div className="hero-banner__specs" key={item.key}>
                        <RichText
                          field={{ value: item.key }}
                          tag="div"
                          className={`hero-banner__label hero-banner__label--${model?.quickSpecsColor} rich-text`}
                        ></RichText>
                        <RichText
                          field={{ value: item.value }}
                          tag="div"
                          className={`hero-banner__value hero-banner__value--${model?.quickSpecsColor} rich-text`}
                        ></RichText>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeDown>
            ) : null}
      {!!(isMobile && (model?.primaryLink?.label || model?.enableBuildPriceCTA && buildAndPriceUrl)) && (
            <div className="hero-banner__mobile-only">
          {model?.enableBuildPriceCTA && (
                <CTA type={ctaTypes.primaryOrange}
                  extraClassName="hero-banner__mobile-only__cta"
                  href={appendRootToInternalLink(buildAndPriceUrl)}
                >
                  <DictionaryTerm keyword={'cnhi-build-price'} />
                </CTA>
              )}
          {(isSpecialOfferCTAExist ? (
        <div className="hero-banner__mobile-only">
          <CTA onClick={() => handleOnClick()} type={ctaTypes.primaryOrange} extraClassName="hero-banner__mobile-only__cta">
            {model?.primaryLink?.label}
          </CTA>
        </div>
      ) : (
        model.isPrimaryLinkExist && (
          <div className="hero-banner__mobile-only">
                <CTA
                  type={ctaTypes.primaryOrange}
                  extraClassName="hero-banner__mobile-only__cta"
                  field={{
                    value: {
                      href: model.primaryCTAUrl,
                      text: model.primaryLink?.label,
                    },
                  }}
                />
          </div>
        )
      ))}
            </div>
          )}
    </div >
  );
};

export default withTranslation()(withSitecoreContext()(ProductHeroBannerSeriesDetail));
